import { observable, computed, action, runInAction } from 'mobx'
import { Subject, firstValueFrom } from 'rxjs'
import { concatMap, find, share } from 'rxjs/operators'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { CursorPageQuery, CursorPaged } from '@/infra/paging'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Powers Skill and Role management. They both
 * work the same way.
 */
export class SelectionViewModel<T> implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Subject for change requests.
   */
  private readonly _requestedOperations = new Subject<Operation<T>>()

  /**
   * Pipeline for making changes, ensures only one
   * request is happening at once.
   * Emits the requested change.
   */
  private readonly _completedOperations$ = this._requestedOperations.pipe(
    concatMap((change) => this._performOperation(change)),
    share(),
  )

  private _hasFetchedItems = false

  /**
   * Items with a toggle in flight.
   */
  private readonly _pendingToggles = observable.set<T>()

  /**
   * Search text.
   */
  @observable accessor searchText = ''

  /**
   * The editor dialog.
   */
  readonly modal = new ModalViewModel()

  /**
   * Scroll provider used to manage infinite scrolling state.
   */
  readonly scrollProvider = new InfiniteScrollProvider({
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._options.createAlertRetryWrapper(
        'Loading options failed',
        signal,
      )
      return withRetry(() => this._options.fetch(paging, signal))
    },
    limit: 100,
  })

  constructor(private readonly _options: SelectionViewModelOptions<T>) {}

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this._hasFetchedItems = false
  }

  /**
   * All items from the scroll provider.
   */
  @computed
  get allItems(): T[] {
    return this.scrollProvider.items.slice()
  }

  /**
   * The selected items.
   */
  @computed
  get selectedItems(): T[] {
    return this._options.selection()
  }

  /**
   * Gets the filtered items.
   *
   * A value is included in the matching items
   * if all parts of the search text have a partial
   * match with any parts of the item
   *
   * E.g
   * items = [['Something', 'to look for'], ['slow sound'], ['slow']]
   * searchText = "so lo"
   *
   * Will match return [[Something', 'to look for'], ['slow sound']]
   */
  @computed
  get filteredItems() {
    const normalized = this.searchText.toLowerCase().trim()
    if (!normalized) {
      return this.allItems
    }

    const searchParts = normalized.split(' ')
    return this.allItems.filter((item) => {
      const itemParts = this._options
        .getTextSearchValues(item)
        .filter((v) => v !== '')
        .map((p) => p.toLocaleLowerCase())

      let matchingParts = 0
      for (const searchPart of searchParts) {
        const matches = itemParts.some((ip) => ip.includes(searchPart))
        if (matches) {
          matchingParts++
        }
      }

      // Include in results if all search parts had a partial match
      return matchingParts === searchParts.length
    })
  }

  /**
   * Check if the item is selected.
   * Used by the all-list, shouldn't be called until items have been fetched.
   * @param item
   */
  isSelected(item: T) {
    return this.selectedItems.includes(item)
  }

  /**
   * Gets item ID.
   *
   * @param item
   */
  getItemId(item: T) {
    return this._options.getItemId(item)
  }

  /**
   * Whether we are allowed to perform an operation on this item.
   *
   * @param item
   */
  canUpdate(item: T) {
    return this._options.canUpdate(item)
  }

  /**
   * Sets the search text.
   *
   * @param searchText
   */
  @action.bound
  setSearchText(searchText: string) {
    this.searchText = searchText
  }

  /**
   * Shows the editor. The available items are fetched the first time, and
   * the list shows its own pending state.
   */
  @action.bound
  showEditor() {
    this.modal.show()
    if (!this._hasFetchedItems) {
      this._hasFetchedItems = true
      this.fetchItems()
    }
  }

  /**
   * Fetches the available items.
   */
  @action.bound
  fetchItems() {
    // The list shows the failure.
    this.scrollProvider
      .fetch({ signal: this._lifecycle.signal })
      .catch(() => undefined)
  }

  /**
   * Adds an item.
   */
  async add(item: T) {
    return this._queueOp({
      item,
      op: 'Add',
    })
  }

  /**
   * Removes an item.
   */
  async remove(item: T) {
    return this._queueOp({
      item,
      op: 'Remove',
    })
  }

  /**
   * Whether a toggle of the item is in flight.
   *
   * @param item
   */
  isTogglePending(item: T) {
    return this._pendingToggles.has(item)
  }

  /**
   * Toggle the selection.
   */
  @action.bound
  async toggle(item: T) {
    this._pendingToggles.add(item)
    try {
      await (this.isSelected(item) ? this.remove(item) : this.add(item))
    } finally {
      runInAction(() => this._pendingToggles.delete(item))
    }
  }

  /**
   * Performs the operation.
   * @param change
   */
  private async _performOperation(change: Operation<T>) {
    await (
      change.op === 'Add'
        ? this._options.add(change.item)
        : this._options.remove(change.item)
    )
      .catch(
        this._options.createAlertErrorHandler(
          change.op === 'Add' ? 'Adding failed' : 'Removing failed',
        ),
      )
      .catch(() => undefined)
    return change
  }

  /**
   * Queues an operation, returns a Promise that resolves when it is done.
   *
   * @param operation
   */
  private _queueOp(operation: Operation<T>) {
    const promise = firstValueFrom(
      this._completedOperations$.pipe(find((c) => c === operation)),
      { defaultValue: undefined },
    )
    this._requestedOperations.next(operation)
    return promise
  }
}

/**
 * Options for {@link SelectionViewModel}.
 */
export interface SelectionViewModelOptions<T> {
  /**
   * Creates a handler that alerts a failed change and rethrows.
   */
  createAlertErrorHandler: AlertErrorHandlerFactory
  /**
   * Creates a wrapper that alerts a failed fetch with the option to retry.
   */
  createAlertRetryWrapper: AlertRetryWrapperFactory
  /**
   * Delegate for getting the item ID.
   * @param item
   */
  getItemId(item: T): string
  /**
   * Delegate for whether we are allowed to change the item.
   * @param item
   */
  canUpdate(item: T): boolean
  /**
   * Delegate for fetching all the available items.
   */
  fetch(query: CursorPageQuery, signal: AbortSignal): Promise<CursorPaged<T>>
  /**
   * The currently selected items. Read in a computed, so it can be reactive.
   */
  selection(): T[]
  /**
   * Delegate for adding an item.
   * The computed selection should change after this.
   *
   * @param item
   */
  add(item: T): Promise<unknown>
  /**
   * Delegate for removing an item.
   * The computed selection should change after this.
   *
   * @param item
   */
  remove(item: T): Promise<unknown>
  /**
   * Delegate for getting an array of text values that can be searched.
   *
   * @param item
   */
  getTextSearchValues(item: T): string[]
}

/**
 * Selection operation.
 */
type SelectionOperation = 'Add' | 'Remove'

/**
 * Requested change.
 */
interface Operation<T> {
  op: SelectionOperation
  item: T
}
